import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@apollo/client";

import Button from "../../atoms/Button";
import Title from "../../atoms/Title";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import OffCanvasTrayBody from "../../molecules/OffCanvasTrayBody";
import OffCanvasTrayClose from "../../atoms/OffCanvasTrayClose";
import WineCellarVintageOptionTray from "../WineCellarVintageOptionTray";

import { GetWinePhotos_wine } from "../../../types/__generated__/GetWinePhotos";
import { RequestWineStock, RequestWineStockVariables } from "../../../types/__generated__/RequestWineStock";
import { getWineBaseColor } from "../../../helpers/tools";
import { logError } from "../../../helpers/logging";
import { GetLatestVintage } from "../../../types/__generated__/GetLatestVintage";
import { showErrorAlert, showSuccessAlert } from "../../../helpers/alerts";
import defaultBottleImage from "../../../assets/images/temp/default-bottle.png";
import { I18N_NAMESPACES, FETCH_POLICY_CACHE_ONLY } from "../../../helpers/constants";
import {
  GET_LATEST_VINTAGE,
  REQUEST_WINE_STOCK,
  GET_WINE_NAME_AND_LARGE_PHOTO,
} from "../../../graphql/resolvers/wine";
import { GET_AUTH } from "../../../graphql/resolvers/auth";

import {
  BackgroundImage,
  BackgroundImageContainer,
  BottleImage,
  BottleImageWrapper,
  BottleName,
  BottleWrapper,
  TrayContentContainer,
  Text,
  StyledOffCanvasTrayHeaderTitle,
  StyledOffCanvasTrayHeader,
} from "./style";

interface SoldOutProps {
  wine: GetWinePhotos_wine;
  onCloseTray: () => void;
}

const SoldOutOptions = ({ wine, onCloseTray }: SoldOutProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.WINE_PAGES]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wineRequestSubmitted, setWineRequestSubmitted] = useState(wine?.stockNotificationRequested);
  const [isVintageTrayOpened, setIsVintageTrayOpened] = useState(false);

  const { data: latestVintageData, loading: isVintageDataLoading } = useQuery<GetLatestVintage>(
    GET_LATEST_VINTAGE,
    {
      skip: !wine.hasOtherVintageStock,
      variables: { wineSeriesId: wine?.wineSeries?.id },
      partialRefetch: true,
    }
  );
  const { data: authData, loading: authLoading } = useQuery(GET_AUTH, {
    fetchPolicy: FETCH_POLICY_CACHE_ONLY,
  });
  const memberId = authData?.auth?.memberId || null;
  const [requestWineStock] = useMutation<RequestWineStock, RequestWineStockVariables>(REQUEST_WINE_STOCK);

  const loading = isVintageDataLoading || authLoading || isSubmitting;
  if (loading) {
    return <StyledLoading />;
  }

  const shouldShowNotificationConfirmation = !wine.hasOtherVintageStock && wineRequestSubmitted;
  const { latestVintage } = latestVintageData || {};
  const vintageAvailable = !!latestVintage;
  const vintageTrayTitle = vintageAvailable
    ? t("winePages:soldOut.vintageAvailable")
    : t("winePages:soldOut.weAreSorry");
  const trayTitle = shouldShowNotificationConfirmation ? t("winePages:notify.inTouch") : vintageTrayTitle;

  const onSubscribe = () => {
    setIsSubmitting(true);
    requestWineStock({
      variables: {
        input: {
          memberId,
          wineId: wine?.id!,
        },
      },
      refetchQueries: [
        { query: GET_WINE_NAME_AND_LARGE_PHOTO, variables: { slug: wine.product?.slug, memberId } },
      ],
    })
      .then(() => {
        showSuccessAlert(t("winePages:notify.success"));
        setIsSubmitting(false);
        setWineRequestSubmitted(true);
      })
      .catch((error) => {
        showErrorAlert(t("common:sorry"));
        setIsSubmitting(false);
        logError(error);
      });
  };

  const wineTypeId = latestVintage?.wineType?.id || 0;
  const wineClassId = latestVintage?.wineType?.wineClass?.id || 0;
  const productPhotos = latestVintage?.product?.productPhotos || [];

  const renderSoldoutState = () =>
    vintageAvailable ? (
      <BottleWrapper onClick={() => setIsVintageTrayOpened(true)}>
        <BottleImageWrapper backgroundColor={getWineBaseColor(wineClassId, wineTypeId)}>
          <BottleImage
            src={productPhotos.length > 0 ? productPhotos[0].photoSmall || "" : defaultBottleImage}
          />
        </BottleImageWrapper>
        <BottleName>{latestVintage?.product?.name}</BottleName>
      </BottleWrapper>
    ) : (
      <>
        <Text>{t("winePages:soldOut.message")}</Text>
        <Button type="submit" onClick={onSubscribe} full lg>
          {t("winePages:soldOut.notifyMe")}
        </Button>
        <BackgroundImageContainer>
          <BackgroundImage />
        </BackgroundImageContainer>
      </>
    );

  const renderNotificationConfirmation = () => (
    <>
      <Text>{t("winePages:notify.inTouchMessage")}</Text>
      <Button onClick={onCloseTray} full outline lg>
        {t("winePages:notify.close")}
      </Button>
      <BackgroundImageContainer>
        <BackgroundImage />
      </BackgroundImageContainer>
    </>
  );

  const shouldShowVintageOptionTray = isVintageTrayOpened && latestVintage;

  return (
    <>
      <StyledOffCanvasTrayHeader noBottomSpacing={!vintageAvailable}>
        <OffCanvasTrayClose onClick={onCloseTray} />
        <StyledOffCanvasTrayHeaderTitle>{trayTitle}</StyledOffCanvasTrayHeaderTitle>
      </StyledOffCanvasTrayHeader>

      <OffCanvasTrayBody>
        <TrayContentContainer>
          <Title tag={2} uppercase hiddenSm>
            {trayTitle}
          </Title>
          {shouldShowNotificationConfirmation ? renderNotificationConfirmation() : renderSoldoutState()}
          {shouldShowVintageOptionTray && (
            <WineCellarVintageOptionTray
              wine={latestVintage}
              onCloseTray={() => setIsVintageTrayOpened(false)}
            />
          )}
        </TrayContentContainer>
      </OffCanvasTrayBody>
    </>
  );
};

export default SoldOutOptions;
